import { BitmapLayer } from "@deck.gl/layers";
import { useQuery } from "@tanstack/react-query";
import { GeoTIFFImage, ReadRasterResult } from "geotiff";
import { fromArrayBuffer } from "geotiff";
import proj4 from "proj4";
import { useMemo } from "react";

import { hexToRgb } from "@nens/shared-utils";

import { MapResult } from "@/api";
import { Legend } from "@/api/accurate-types";
import getTbiApi from "@/api/api-v1";

// YOU CAN FIND THE EXPLANATION BEFORE THE EXPORTED HOOK

// Fetch the geotiff file and get the image and raster data
const getGeoTIFFQuery = (url?: string) =>
  getTbiApi().getQueryOptions({
    queryKey: ["geo-tiff", url],
    queryFn: async () => {
      if (!url) throw new Error("Never happens");
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch GeoTIFF: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      const tiff = await fromArrayBuffer(arrayBuffer);
      const image = await tiff.getImage();

      const raster = await image.readRasters({ interleave: true });

      return { image, raster };
    },
    enabled: !!url,
  });

// Project the image bounding box to lng/lat
const RD =
  'PROJCS["Amersfoort / RD New", GEOGCS["Amersfoort", DATUM["Amersfoort", SPHEROID["Bessel 1841",6377397.155,299.1528128, AUTHORITY["EPSG","7004"]], TOWGS84[565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725], AUTHORITY["EPSG","6289"]], PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4289"]],PROJECTION["Oblique_Stereographic"],PARAMETER["latitude_of_origin",52.15616055555555],PARAMETER["central_meridian",5.38763888888889],PARAMETER["scale_factor",0.9999079],PARAMETER["false_easting",155000],PARAMETER["false_northing",463000], UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["X",EAST], AXIS["Y",NORTH],AUTHORITY["EPSG","28992"]]';
proj4.defs("EPSG:28992", RD);
function mercatorToLngLat(lng: number, lat: number) {
  return proj4("EPSG:3857", "WGS84", [lng, lat]);
}

const getBoundsFromGeoTIFF = (image: GeoTIFFImage): [number, number, number, number] => {
  const bbox = image.getBoundingBox();

  const southWest = mercatorToLngLat(bbox[0], bbox[1]);
  const northEast = mercatorToLngLat(bbox[2], bbox[3]);

  return [southWest[0], southWest[1], northEast[0], northEast[1]];
};

// Extract raster data (assume backend always provides correct format)
function extractRasterData(raster: ReadRasterResult): Float32Array {
  return Array.isArray(raster) ? (raster[0] as Float32Array) : (raster as Float32Array);
}

// Create color mapping function based on legend
function createColorMapper(legend: Legend) {
  return (value: number): [number, number, number] => {
    const legendEntries = legend.legend;

    // Find which legend segment this value falls into
    for (let i = 0; i < legendEntries.length - 1; i++) {
      const current = legendEntries[i];
      const next = legendEntries[i + 1];

      if (value >= current.value && value <= next.value) {
        // Interpolate between colors
        const t = (value - current.value) / (next.value - current.value);
        const currentRGB = hexToRgb(current.color);
        const nextRGB = hexToRgb(next.color);

        return [
          Math.round(currentRGB[0] + (nextRGB[0] - currentRGB[0]) * t),
          Math.round(currentRGB[1] + (nextRGB[1] - currentRGB[1]) * t),
          Math.round(currentRGB[2] + (nextRGB[2] - currentRGB[2]) * t),
        ];
      }
    }

    // Use first or last color as fallback
    const fallbackColor =
      value < legendEntries[0].value
        ? legendEntries[0].color
        : legendEntries[legendEntries.length - 1].color;

    return hexToRgb(fallbackColor);
  };
}

// Apply colors to Float32Data data with no-data value handling
function applyColorsToFloat32Data(
  rasterData: Float32Array,
  width: number,
  height: number,
  getColor: (value: number) => [number, number, number],
  transparentValue?: number
): Uint8ClampedArray {
  const maxReasonableValue = 9000; // Filter out no-data values
  const rgba = new Uint8ClampedArray(width * height * 4);

  for (let i = 0; i < rasterData.length; i++) {
    const value = rasterData[i];
    const pixelIndex = i * 4;

    const setRGBA = (r: number, g: number, b: number, a: number) => {
      rgba[pixelIndex] = r;
      rgba[pixelIndex + 1] = g;
      rgba[pixelIndex + 2] = b;
      rgba[pixelIndex + 3] = a;
    };

    if (
      isFinite(value) &&
      (!transparentValue || transparentValue !== value) &&
      Math.abs(value) < maxReasonableValue
    ) {
      const [r, g, b] = getColor(value);
      setRGBA(r, g, b, 255);
    } else {
      setRGBA(0, 0, 0, 0);
    }
  }

  return rgba;
}

// EXPLANATION
// Get the geotiff file from the backend, process it and return a deck.gl BitmapLayer
// Processing includes converting coordinates and applying color mapping based on the provided legend

export const useGeoTIFFLayer = (mapResult?: MapResult) => {
  const geoTiffQuery = useQuery(getGeoTIFFQuery(mapResult?.geotiffUrl));

  return useMemo(() => {
    if (!geoTiffQuery.isSuccess || !mapResult) return null;

    const legend = mapResult.legend as Legend;
    const transparentValue = mapResult.transparentValue ?? undefined;

    const { image, raster } = geoTiffQuery.data;

    // Get metadata
    const width = image.getWidth();
    const height = image.getHeight();

    // Extract bounds from GeoTIFF image
    const bounds = getBoundsFromGeoTIFF(image);

    // Process raster data (assume Float32Array from backend)
    const rasterData = extractRasterData(raster) as Float32Array;
    const getColor = createColorMapper(legend);
    const rgba = applyColorsToFloat32Data(rasterData, width, height, getColor, transparentValue);

    // Create canvas
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(rgba);
    ctx.putImageData(imageData, 0, 0);

    // Create deck.gl layer
    return new BitmapLayer({
      id: "geotiff-bitmap",
      image: canvas,
      bounds,
    });
  }, [geoTiffQuery.data, geoTiffQuery.isSuccess, mapResult]);
};
